import { GenerationFailureCard } from "./GenerationFailureCard";
import { DraftGuidance } from "./DraftGuidance";
import { PostViewer } from "./PostViewer";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  queryEqual,
  Query,
  QueryConstraint,
  where,
} from "firebase/firestore";
import {
  ArrowRight,
  Copy,
  FileText,
  Globe,
  ImageIcon,
  Loader2,
  MoreVertical,
  Pencil,
  Send,
  Play,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import posthog from "posthog-js";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCollection } from "react-firebase-hooks/firestore";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useStripe } from "../contexts/StripeContext";
import {
  isCheckoutContinuationUsable,
  useCheckoutContinuation,
} from "../hooks/useCheckoutContinuation";
import { usePostActions } from "../hooks/usePostActions";
import { useDraftGalleryAutoFill } from "../hooks/useDraftGalleryAutoFill";
import { cn, loadMediaUrls } from "../lib/utils";
import { editorNavigationState } from "../lib/editorBackgroundRoute";
import {
  compareDraftTimelinePosts,
  selectDraftTimelineWindow,
} from "../lib/draftTimeline";
import { PlatformConnection, PlatformType } from "../models/Platform";
import { Post, isVideoPost } from "../models/Post";
import { postConverter } from "../services/converters";
import { derivePostStatus, isPostEditable } from "../utils/postStatus";
import { db } from "../services/Firebase";
import { GeneratingPostMedia } from "./GeneratingPostMedia";
import { HorizontalPostShelf } from "./HorizontalPostShelf";
import { PendingGenerationTile } from "./PendingGenerationTile";
import {
  generationRequestDocId,
  generationFailurePatch,
  PendingRequest,
  PendingSource,
  readPendingRequests,
  removePendingRequest,
  shouldReportPendingProgressFailure,
  updatePendingRequest,
} from "../lib/pendingGeneration";
import {
  isDraftGalleryPost,
  showSourcePlaceholder,
  buildDraftSourceGroups,
  buildDraftSourceGroupsForDay,
  PendingGenerationProgress,
} from "../lib/draftSourceGroups";
import MediaGallery from "./MediaGallery";
import { PlatformConnectionIcon } from "./PlatformConnectionIcon";
import { useWorkspaceConnections } from "../hooks/useWorkspaceConnections";
import { StatusBadge } from "./StatusBadge";
import { useUpgradePage } from "../hooks/useUpgradePage";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const PAGE_SIZE = 12; // Number of posts to fetch per page
const REQUEST_COMPLETION_MAX_ATTEMPTS = 4;

// Keep each workspace's place while navigating within the app. The embedded
// Recent drafts section must not share the standalone Drafts page's position.
const draftViewPositions = new Map<string, {
  scrollY: number;
  limitCount: number;
  queryLimit: number;
}>();

const SOURCE_ICONS = {
  pdf: FileText,
  url: Globe,
  text: FileText,
  youtube: Play,
} as const;
type SourceMetadata = { title?: string; favicon?: string };
const legacySourceMetadataCache = new Map<string, SourceMetadata>();

function formatGenerationTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const hasSameRenderedPostState = (
  cachedPost: Post,
  nextPost: Omit<Post, "id">,
) =>
  cachedPost.updatedAt === nextPost.updatedAt &&
  cachedPost.status === nextPost.status &&
  cachedPost.generationError === nextPost.generationError &&
  isVideoPost(cachedPost) === isVideoPost(nextPost) &&
  cachedPost.videoChoreographyHash === nextPost.videoChoreographyHash &&
  cachedPost.videoMusicPath === nextPost.videoMusicPath &&
  cachedPost.videoAudioMuted === nextPost.videoAudioMuted &&
  cachedPost.videoSpeed === nextPost.videoSpeed;

export default function ContentGallery({
  todayOnly = false,
  recentGroupLimit,
  embedded = false,
}: {
  todayOnly?: boolean;
  recentGroupLimit?: number;
  embedded?: boolean;
}) {
  const [todayStart, setTodayStart] = useState(() =>
    new Date().setHours(0, 0, 0, 0),
  );
  useEffect(() => {
    if (!todayOnly) return;
    const timer = window.setInterval(
      () => setTodayStart(new Date().setHours(0, 0, 0, 0)),
      30000,
    );
    return () => window.clearInterval(timer);
  }, [todayOnly]);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, customerData, activeWorkspaceId } = useAuth();
  const draftViewKey = !embedded && currentUser && activeWorkspaceId
    ? `${currentUser.uid}:${activeWorkspaceId}:${todayOnly}`
    : null;
  // A just-submitted request shows at the top, so don't restore an old scroll.
  const fromGeneration = (location.state as { fromGeneration?: boolean } | null)?.fromGeneration === true;
  const cachedDraftView = draftViewKey && !fromGeneration ? draftViewPositions.get(draftViewKey) : undefined;
  const restoredDraftViewKey = useRef<string | null>(null);
  const {
    createCheckoutSession,
    createCreditCheckoutSession,
    resumeCheckoutSession,
    isFreePlan,
    isLoading: planLoading,
  } = useStripe();
  const { handleEdit, getDeleteDialog, handleDuplicate } = usePostActions();

  const [creditsBannerDismissed, setCreditsBannerDismissed] = useState(false);
  const { navigateToUpgrade } = useUpgradePage();

  const [internalHasMore, setInternalHasMore] = useState(true);
  const [limitCount, setLimitCount] = useState(cachedDraftView?.limitCount ?? PAGE_SIZE);
  const [queryLimit, setQueryLimit] = useState(cachedDraftView?.queryLimit ?? PAGE_SIZE);

  useLayoutEffect(() => {
    restoredDraftViewKey.current = null;
    if (!draftViewKey) return;
    const saved = fromGeneration ? undefined : draftViewPositions.get(draftViewKey);
    setLimitCount(saved?.limitCount ?? PAGE_SIZE);
    setQueryLimit(saved?.queryLimit ?? PAGE_SIZE);
  }, [draftViewKey, fromGeneration]);

  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(
    new Set(),
  );

  const previousPostCountRef = useRef(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [legacySourceMetadata, setLegacySourceMetadata] = useState<
    Record<string, SourceMetadata>
  >({});
  const attemptedLegacySourcesRef = useRef(new Set<string>());

  // Gallery-card previews are local; URL deep links are owned by DraftsRoute.
  const [selectedPostForViewer, setSelectedPostForViewer] = useState<Post | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutContinuation = useCheckoutContinuation(customerData);

  const handleCheckoutContinuation = async () => {
    if (!checkoutContinuation) return;

    const fallbackToFreshCheckout = async () => {
      if (!checkoutContinuation.priceId) {
        navigateToUpgrade({
          reason:
            checkoutContinuation.mode === "payment"
              ? "no_credits"
              : "sidebar_cta",
        });
        return;
      }

      if (checkoutContinuation.mode === "subscription") {
        await createCheckoutSession(checkoutContinuation.priceId);
        return;
      }

      await createCreditCheckoutSession(checkoutContinuation.priceId);
    };

    if (!isCheckoutContinuationUsable(checkoutContinuation)) {
      await fallbackToFreshCheckout();
      return;
    }

    try {
      if (
        checkoutContinuation.kind === "recovery" &&
        checkoutContinuation.recoveryUrl
      ) {
        window.location.href = checkoutContinuation.recoveryUrl;
        return;
      }

      await resumeCheckoutSession(checkoutContinuation.sessionId);
    } catch {
      await fallbackToFreshCheckout();
    }
  };

  const toggleDescription = (postId: string) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  const { connections: workspaceConnections, loading: connectionsLoading, error: connectionsError } = useWorkspaceConnections();

  const connectionsById = useMemo(() => {
    return Object.fromEntries(
      workspaceConnections.map((connection) => [connection.id, connection]),
    ) as Record<string, PlatformConnection>;
  }, [workspaceConnections]);

  // Function to build the Firestore query
  const buildQuery = useCallback(
    (currentLimit: number) => {
      if (!currentUser) return null;

      const typedCollection = collection(db, "posts").withConverter(
        postConverter,
      ) as Query<Post>; // Type the collection

      const constraints: QueryConstraint[] = [
        where("userId", "==", activeWorkspaceId),
        // Count failed results toward completed requests; grouping hides their cards.
        where("status", "in", ["draft", "generating", "failed"]),
      ];

      if (todayOnly) {
        constraints.push(where("createdAt", ">=", todayStart));
        constraints.push(
          where("createdAt", "<", new Date(todayStart).setHours(24, 0, 0, 0)),
        );
      }

      // Keep pagination aligned with the immutable row timeline. Post edits
      // change updatedAt, but must never move a source row in the gallery.
      constraints.push(
        orderBy("createdAt", "desc"),
        orderBy("__name__", "desc"), // Secondary sort for consistent ordering
      );
      // Keep one lookahead document so the UI only shows Load more when
      // another post actually exists.
      constraints.push(limit(currentLimit + 1));

      // Removed startAfter logic as limitCount approach handles progressive loading

      return query(typedCollection, ...constraints); // Use typedCollection
    },
    [currentUser, activeWorkspaceId, todayOnly, todayStart],
  );

  // Memoize the query to prevent unnecessary re-renders for useCollectionData
  const postsQuery = useMemo(
    () => buildQuery(queryLimit),
    [queryLimit, buildQuery],
  );

  // Switch to useCollection to get the snapshot directly
  const [snapshot, loadingData, errorData] = useCollection(postsQuery);

  // Maintain stable post references to prevent unnecessary re-renders
  const postsMapRef = useRef<Map<string, Post>>(new Map());

  const snapshotMatchesQuery = Boolean(
    snapshot && postsQuery && queryEqual(snapshot.query, postsQuery),
  );

  const fetchedPosts: Post[] = useMemo(() => {
    if (!snapshot || !snapshotMatchesQuery) return [];

    const newPosts: Post[] = [];
    snapshot.docs.slice(0, queryLimit).forEach((doc) => {
      const postId = doc.id;
      const postData = doc.data() as Omit<Post, "id">;
      if (!isDraftGalleryPost(postData)) return;
      const existingPost = postsMapRef.current.get(postId);

      if (existingPost && hasSameRenderedPostState(existingPost, postData)) {
        newPosts.push(existingPost);
      } else {
        const newPost = { ...postData, id: postId } as Post;
        postsMapRef.current.set(postId, newPost);
        newPosts.push(newPost);
      }
    });

    return newPosts;
  }, [queryLimit, snapshot, snapshotMatchesQuery]);

  // A generation can contain several concurrently-created source rows, so a
  // single global-query lookahead cannot prove that a visible row is complete.
  // Resolve each visible request directly (the product currently caps one
  // request at 50 posts), cache it, and merge those exact drafts into the
  // timeline before exposing the page.
  const candidateRequestIds = useMemo(
    () =>
      Array.from(
        new Set(
          [...fetchedPosts]
            .sort(compareDraftTimelinePosts)
            .slice(0, limitCount)
            .flatMap((post) =>
              post.generationRequestId ? [post.generationRequestId] : [],
            ),
        ),
      ).sort(),
    [fetchedPosts, limitCount],
  );
  const candidateRequestIdsKey = candidateRequestIds.join("|");
  const requestCompletionUnsubscribesRef = useRef(
    new Map<string, () => void>(),
  );
  const requestCompletionRetryTimersRef = useRef(new Map<string, number>());
  const requestCompletionAttemptCountsRef = useRef(new Map<string, number>());
  const [requestCompletionPosts, setRequestCompletionPosts] = useState<
    Record<string, Post[]>
  >({});
  const [requestCompletionErrors, setRequestCompletionErrors] = useState<
    Record<string, Error>
  >({});

  useEffect(
    () => () => {
      requestCompletionUnsubscribesRef.current.forEach((unsubscribe) =>
        unsubscribe(),
      );
      requestCompletionUnsubscribesRef.current.clear();
      requestCompletionRetryTimersRef.current.forEach((timer) =>
        window.clearTimeout(timer),
      );
      requestCompletionRetryTimersRef.current.clear();
      requestCompletionAttemptCountsRef.current.clear();
    },
    [activeWorkspaceId],
  );

  useEffect(() => {
    // During a query-limit transition, react-firebase-hooks temporarily keeps
    // the old snapshot. Wait for it to match before reconciling, otherwise an
    // empty transitional candidate list would churn every active listener.
    if (!currentUser || !activeWorkspaceId || !snapshotMatchesQuery) return;

    const workspacePrefix = `${activeWorkspaceId}:`;
    const candidateCacheKeys = new Set(
      candidateRequestIds.map((requestId) => `${workspacePrefix}${requestId}`),
    );
    requestCompletionUnsubscribesRef.current.forEach(
      (unsubscribe, cacheKey) => {
        if (
          cacheKey.startsWith(workspacePrefix) &&
          !candidateCacheKeys.has(cacheKey)
        ) {
          unsubscribe();
          requestCompletionUnsubscribesRef.current.delete(cacheKey);
          requestCompletionAttemptCountsRef.current.delete(cacheKey);
        }
      },
    );
    requestCompletionRetryTimersRef.current.forEach((timer, cacheKey) => {
      if (
        cacheKey.startsWith(workspacePrefix) &&
        !candidateCacheKeys.has(cacheKey)
      ) {
        window.clearTimeout(timer);
        requestCompletionRetryTimersRef.current.delete(cacheKey);
        requestCompletionAttemptCountsRef.current.delete(cacheKey);
      }
    });
    requestCompletionAttemptCountsRef.current.forEach((_, cacheKey) => {
      if (
        cacheKey.startsWith(workspacePrefix) &&
        !candidateCacheKeys.has(cacheKey)
      ) {
        requestCompletionAttemptCountsRef.current.delete(cacheKey);
      }
    });
    setRequestCompletionPosts((previous) => {
      const next = Object.fromEntries(
        Object.entries(previous).filter(
          ([cacheKey]) =>
            !cacheKey.startsWith(workspacePrefix) ||
            candidateCacheKeys.has(cacheKey),
        ),
      );
      return Object.keys(next).length === Object.keys(previous).length
        ? previous
        : next;
    });
    setRequestCompletionErrors((previous) => {
      const next = Object.fromEntries(
        Object.entries(previous).filter(
          ([cacheKey]) =>
            !cacheKey.startsWith(workspacePrefix) ||
            candidateCacheKeys.has(cacheKey),
        ),
      );
      return Object.keys(next).length === Object.keys(previous).length
        ? previous
        : next;
    });

    const subscribe = (requestId: string) => {
      const cacheKey = `${activeWorkspaceId}:${requestId}`;
      if (
        requestCompletionUnsubscribesRef.current.has(cacheKey) ||
        requestCompletionRetryTimersRef.current.has(cacheKey)
      ) {
        return;
      }

      const typedCollection = collection(db, "posts").withConverter(
        postConverter,
      ) as Query<Post>;
      const unsubscribe = onSnapshot(
        query(
          typedCollection,
          where("userId", "==", activeWorkspaceId),
          where("generationRequestId", "==", requestId),
        ),
        (requestSnapshot) => {
          requestCompletionAttemptCountsRef.current.delete(cacheKey);
          setRequestCompletionErrors((previous) => {
            if (!Object.prototype.hasOwnProperty.call(previous, cacheKey)) {
              return previous;
            }
            const next = { ...previous };
            delete next[cacheKey];
            return next;
          });
          const requestPosts = requestSnapshot.docs.flatMap((postDoc) => {
            const data = postDoc.data() as Omit<Post, "id">;
            if (!isDraftGalleryPost(data)) {
              return [];
            }
            const existingPost = postsMapRef.current.get(postDoc.id);
            const post =
              existingPost && hasSameRenderedPostState(existingPost, data)
                ? existingPost
                : ({ ...data, id: postDoc.id } as Post);
            postsMapRef.current.set(postDoc.id, post);
            return [post];
          });
          setRequestCompletionPosts((previous) => ({
            ...previous,
            [cacheKey]: requestPosts,
          }));
        },
        (error) => {
          const failedAttempts =
            (requestCompletionAttemptCountsRef.current.get(cacheKey) || 0) + 1;
          requestCompletionAttemptCountsRef.current.set(
            cacheKey,
            failedAttempts,
          );
          console.warn(
            `Draft generation listener failed for ${requestId} ` +
              `(attempt ${failedAttempts}/${REQUEST_COMPLETION_MAX_ATTEMPTS}):`,
            error,
          );
          requestCompletionUnsubscribesRef.current.delete(cacheKey);
          if (failedAttempts >= REQUEST_COMPLETION_MAX_ATTEMPTS) {
            setRequestCompletionErrors((previous) => ({
              ...previous,
              [cacheKey]: error,
            }));
            return;
          }
          const timer = window.setTimeout(() => {
            requestCompletionRetryTimersRef.current.delete(cacheKey);
            subscribe(requestId);
          }, 3000);
          requestCompletionRetryTimersRef.current.set(cacheKey, timer);
        },
      );
      requestCompletionUnsubscribesRef.current.set(cacheKey, unsubscribe);
    };

    candidateRequestIds.forEach((requestId) => {
      subscribe(requestId);
    });
    // The sorted key represents the complete candidate set; listeners are
    // reconciled as the visible page changes and cleaned up on user change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateRequestIdsKey, activeWorkspaceId, snapshotMatchesQuery]);

  const completedRequestIds = useMemo(() => {
    if (!activeWorkspaceId) return new Set<string>();
    return new Set(
      candidateRequestIds.filter((requestId) =>
        Object.prototype.hasOwnProperty.call(
          requestCompletionPosts,
          `${activeWorkspaceId}:${requestId}`,
        ),
      ),
    );
  }, [activeWorkspaceId, candidateRequestIds, requestCompletionPosts]);

  const timelineCandidatePosts = useMemo(() => {
    const merged = new Map(
      fetchedPosts.map((post) => [post.id, post] as const),
    );
    if (activeWorkspaceId) {
      completedRequestIds.forEach((requestId) => {
        requestCompletionPosts[`${activeWorkspaceId}:${requestId}`]?.forEach(
          (post) => merged.set(post.id, post),
        );
      });
    }
    return Array.from(merged.values());
  }, [
    completedRequestIds,
    activeWorkspaceId,
    fetchedPosts,
    requestCompletionPosts,
  ]);

  const waitingForRequestCompletion =
    completedRequestIds.size < candidateRequestIds.length;

  const requestCompletionError = useMemo(() => {
    if (!activeWorkspaceId) return undefined;
    return candidateRequestIds
      .map(
        (requestId) =>
          requestCompletionErrors[`${activeWorkspaceId}:${requestId}`],
      )
      .find(Boolean);
  }, [activeWorkspaceId, candidateRequestIds, requestCompletionErrors]);

  const timelineWindow = useMemo(() => {
    if (!snapshot || !snapshotMatchesQuery) {
      return { posts: [] as Post[], stable: false, hasMore: false };
    }

    const firstUnseenDoc = snapshot.docs[queryLimit];
    const firstUnseenPost = firstUnseenDoc?.data();
    return selectDraftTimelineWindow(
      timelineCandidatePosts,
      limitCount,
      snapshot.docs.length <= queryLimit,
      firstUnseenDoc && firstUnseenPost
        ? {
            id: firstUnseenDoc.id,
            createdAt: firstUnseenPost.createdAt,
            generationSource: firstUnseenPost.generationSource,
            generationRequestId: firstUnseenPost.generationRequestId,
            generationSourceCreatedAt:
              firstUnseenPost.generationSourceCreatedAt,
          }
        : undefined,
      completedRequestIds,
    );
  }, [
    completedRequestIds,
    limitCount,
    queryLimit,
    snapshot,
    snapshotMatchesQuery,
    timelineCandidatePosts,
  ]);
  const lookaheadAlreadyIncluded = Boolean(
    snapshotMatchesQuery &&
      snapshot?.docs[queryLimit] &&
      timelineWindow.posts.some(
        (post) => post.id === snapshot.docs[queryLimit].id,
      ),
  );

  const [settledTimelineWindow, setSettledTimelineWindow] = useState<{
    workspaceId?: string;
    posts: Post[];
  }>({ posts: [] });
  useEffect(() => {
    // Keep the recent preview visible while follow-up queries complete its groups.
    if (!timelineWindow.stable && !(recentGroupLimit && snapshotMatchesQuery && timelineWindow.posts.length)) return;
    setSettledTimelineWindow({
      workspaceId: activeWorkspaceId || undefined,
      posts: timelineWindow.posts,
    });
  }, [activeWorkspaceId, timelineWindow, recentGroupLimit, snapshotMatchesQuery]);

  useEffect(() => {
    if (
      !snapshotMatchesQuery ||
      (timelineWindow.stable && !lookaheadAlreadyIncluded) ||
      waitingForRequestCompletion ||
      loadingData ||
      errorData ||
      requestCompletionError
    ) {
      return;
    }
    setQueryLimit((currentLimit) =>
      currentLimit === queryLimit ? currentLimit + PAGE_SIZE : currentLimit,
    );
  }, [
    errorData,
    loadingData,
    lookaheadAlreadyIncluded,
    queryLimit,
    requestCompletionError,
    snapshotMatchesQuery,
    timelineWindow.stable,
    waitingForRequestCompletion,
  ]);

  const paginatedPosts = useMemo(
    () =>
      timelineWindow.stable || (recentGroupLimit && snapshotMatchesQuery)
        ? timelineWindow.posts
        : settledTimelineWindow.workspaceId === activeWorkspaceId
          ? settledTimelineWindow.posts
          : [],
    [activeWorkspaceId, settledTimelineWindow, timelineWindow, recentGroupLimit, snapshotMatchesQuery],
  );

  // Pending generation requests recorded by GeneratePage. The normal flow
  // arrives after Phase A, but this also supports manual navigation or reload
  // while a request is still in flight. Polled because sessionStorage writes
  // don't emit events in the same tab.
  const [pendingRequestState, setPendingRequestState] = useState<{
    workspaceId: string | null;
    requests: PendingRequest[];
  }>({ workspaceId: null, requests: [] });
  const pendingRequests = useMemo(
    () =>
      pendingRequestState.workspaceId === activeWorkspaceId
        ? pendingRequestState.requests
        : [],
    [activeWorkspaceId, pendingRequestState],
  );
  useEffect(() => {
    const evaluate = () =>
      setPendingRequestState((previous) => {
        const next = readPendingRequests(activeWorkspaceId);
        // Preserve identity when nothing changed — a fresh array every 2s
        // would re-render the whole gallery forever.
        return previous.workspaceId === activeWorkspaceId &&
          JSON.stringify(previous.requests) === JSON.stringify(next)
          ? previous
          : { workspaceId: activeWorkspaceId, requests: next };
      });
    evaluate();
    const interval = window.setInterval(evaluate, 2000);
    window.addEventListener("wavegen:pending-generation-changed", evaluate);
    return () => { window.clearInterval(interval); window.removeEventListener("wavegen:pending-generation-changed", evaluate); };
  }, [activeWorkspaceId, paginatedPosts]);

  // The main gallery query is paginated, but an active request can create more
  // than one page of posts. Subscribe to its concrete IDs so every newly
  // generated post remains visible and completion timing is not capped at 12.
  const pendingPostIds = useMemo(
    () =>
      Array.from(
        new Set(
          pendingRequests.flatMap((request) =>
            Array.isArray(request.postIds) ? request.postIds : [],
          ),
        ),
      ).sort(),
    [pendingRequests],
  );
  const pendingPostIdsKey = pendingPostIds.join("|");
  const [pendingPostsById, setPendingPostsById] = useState<
    Record<string, Post>
  >({});
  useEffect(() => {
    const activeIds = new Set(pendingPostIds);
    setPendingPostsById((previous) =>
      Object.fromEntries(
        Object.entries(previous).filter(([postId]) => activeIds.has(postId)),
      ),
    );
    if (!currentUser || pendingPostIds.length === 0) return;

    const unsubscribes = pendingPostIds.map((postId) =>
      onSnapshot(
        doc(db, "posts", postId),
        (postSnapshot) => {
          const data = postSnapshot.data() as Omit<Post, "id"> | undefined;
          setPendingPostsById((previous) => {
            if (!data || !isDraftGalleryPost(data)) {
              if (!previous[postId]) return previous;
              const next = { ...previous };
              delete next[postId];
              return next;
            }
            const existingPost = postsMapRef.current.get(postId);
            const post =
              existingPost && hasSameRenderedPostState(existingPost, data)
                ? existingPost
                : ({ ...data, id: postId } as Post);
            postsMapRef.current.set(postId, post);
            return previous[postId] === post
              ? previous
              : { ...previous, [postId]: post };
          });
        },
        (error) =>
          console.error(
            `Pending post subscription failed for ${postId}:`,
            error,
          ),
      ),
    );

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    // pendingPostIdsKey represents the sorted session marker IDs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId, pendingPostIdsKey]);

  useEffect(() => {
    if (pendingPostIds.length === 0) return;
    const pendingLimit = PAGE_SIZE + pendingPostIds.length;
    setLimitCount((currentLimit) => Math.max(currentLimit, pendingLimit));
    setQueryLimit((currentLimit) => Math.max(currentLimit, pendingLimit));
  }, [pendingPostIds.length]);

  const posts = useMemo(() => {
    const mergedPosts = new Map(
      paginatedPosts.map((post) => [post.id, post] as const),
    );
    pendingPostIds.forEach((postId) => {
      const post = pendingPostsById[postId];
      if (post) mergedPosts.set(postId, post);
    });
    return Array.from(mergedPosts.values());
  }, [paginatedPosts, pendingPostIds, pendingPostsById]);

  // Per-request server progress: the generation_requests doc carries
  // sourcesProcessed (a source's placeholders exist once it's counted) and
  // the terminal status. Keyed subscriptions; pruning happens via storage so
  // the next poll drops finished requests.
  const [pendingProgress, setPendingProgress] = useState<
    Record<string, PendingGenerationProgress>
  >({});
  const pendingIdsKey = pendingRequests.map((r) => r.clientRequestId).join("|");
  useEffect(() => {
    if (!currentUser || !activeWorkspaceId) return;
    const unsubscribes: (() => void)[] = [];
    const timers: number[] = [];
    let cancelled = false;
    for (const request of pendingRequests) {
      const requestId = request.clientRequestId;
      void generationRequestDocId(activeWorkspaceId, requestId).then(
        (docId) => {
          if (!docId || cancelled) return;
          // The gallery can subscribe before the server's transaction creates
          // the request doc; rules deny reads of missing docs and Firestore
          // listeners DIE PERMANENTLY on error — so retry with a delay until
          // the doc exists (bounded, then the placeholder fallback applies).
          const subscribe = (retriesLeft: number) => {
            if (cancelled) return;
            const unsubscribe = onSnapshot(
              doc(db, "generation_requests", docId),
              (snap) => {
                const data = snap.data();
                if (!data) return;
                const status = data.status as string | undefined;
                // "completed" only ends Phase A — posts finish minutes later,
                // and the click→first-post metric needs the marker alive until
                // then (it's pruned by firstPostSeen + the age filter). Only
                // dead-end statuses remove it here.
                if (status === "empty" || status === "failed") {
                  updatePendingRequest(activeWorkspaceId, requestId, generationFailurePatch(data));
                }
                if (status === "processing" || status === "completed") {
                  const pending = readPendingRequests(activeWorkspaceId).find((item) => item.clientRequestId === requestId);
                  if (pending?.confirming || pending?.error) {
                    updatePendingRequest(activeWorkspaceId, requestId, { confirming: false, error: undefined, suggestion: undefined, fallback: undefined });
                  }
                }
                setPendingProgress((prev) => ({
                  ...prev,
                  [requestId]: {
                    sourcesProcessed:
                      typeof data.sourcesProcessed === "number"
                        ? data.sourcesProcessed
                        : undefined,
                    // A successful read ALWAYS yields an array — empty means
                    // "none processed yet", which must NOT fall through to the
                    // whole-request fallback (that raced placeholder arrival
                    // against the first progress write and hid the shelf).
                    processedSourceIndices: Array.isArray(
                      data.processedSourceIndices,
                    )
                      ? data.processedSourceIndices
                      : typeof data.sourcesProcessed === "number"
                        ? Array.from(
                            { length: data.sourcesProcessed },
                            (_, i) => i,
                          )
                        : [],
                    processedSourceIds: Array.isArray(data.processedSourceIds)
                      ? data.processedSourceIds.filter(
                          (sourceId: unknown): sourceId is string =>
                            typeof sourceId === "string",
                        )
                      : undefined,
                    initiatedPostCountsBySourceId:
                      data.initiatedPostCountsBySourceId &&
                      typeof data.initiatedPostCountsBySourceId === "object"
                        ? Object.fromEntries(
                            Object.entries(
                              data.initiatedPostCountsBySourceId,
                            ).filter(
                              (entry): entry is [string, number] =>
                                typeof entry[1] === "number" && entry[1] >= 0,
                            ),
                          )
                        : undefined,
                    status,
                    stage: data.stage === "reading_sources" || data.stage === "creating_posts" ? data.stage : undefined,
                  },
                }));
              },
              (error) => {
                if (retriesLeft > 0 && !cancelled) {
                  timers.push(
                    window.setTimeout(() => subscribe(retriesLeft - 1), 2000),
                  );
                } else if (shouldReportPendingProgressFailure(retriesLeft)) {
                  console.error(
                    `Pending-shelf progress subscription failed for ${requestId} after all retries:`,
                    error,
                  );
                }
                // Retries exhausted: progress stays undefined and the
                // placeholder-arrival fallback applies.
              },
            );
            unsubscribes.push(unsubscribe);
          };
          subscribe(8);
        },
      );
    }
    return () => {
      cancelled = true;
      unsubscribes.forEach((u) => u());
      timers.forEach((t) => window.clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId, pendingIdsKey]);

  const sourceGroups = useMemo(
    () =>
      todayOnly
        ? buildDraftSourceGroupsForDay(
            posts,
            pendingRequests,
            pendingProgress,
            new Date(todayStart),
          )
        : buildDraftSourceGroups(posts, pendingRequests, pendingProgress),
    [posts, pendingRequests, pendingProgress, todayOnly, todayStart],
  );

  // Old post documents only persisted the source URL, not its article title
  // or favicon. Resolve visible legacy rows through the same lightweight
  // metadata endpoint used by Generate, once per source per app session. A
  // dead or protected URL simply keeps the hostname fallback.
  useEffect(() => {
    if (!currentUser) return;

    const urls = Array.from(
      new Set(
        sourceGroups
          .filter((group) => group.needsSourceMetadata && group.sourceUrl)
          .map((group) => group.sourceUrl!),
      ),
    );
    if (urls.length === 0) return;

    const cachedEntries = urls.flatMap((url) => {
      const metadata = legacySourceMetadataCache.get(url);
      return metadata ? ([[url, metadata]] as const) : [];
    });
    if (cachedEntries.length > 0) {
      setLegacySourceMetadata((previous) => ({
        ...previous,
        ...Object.fromEntries(cachedEntries),
      }));
    }

    const urlsToFetch = urls.filter(
      (url) =>
        !legacySourceMetadataCache.has(url) &&
        !attemptedLegacySourcesRef.current.has(url),
    );
    if (urlsToFetch.length === 0) return;
    urlsToFetch.forEach((url) => attemptedLegacySourcesRef.current.add(url));

    let cancelled = false;
    const resolveMetadata = async () => {
      try {
        const token = await currentUser.getIdToken();
        const chunks: string[][] = [];
        for (let index = 0; index < urlsToFetch.length; index += 10) {
          chunks.push(urlsToFetch.slice(index, index + 10));
        }

        const responses = await Promise.all(
          chunks.map(async (urlsChunk) => {
            const response = await fetch(
              `${process.env.REACT_APP_API_BASE}/api/batch/validate-urls`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ urls: urlsChunk }),
              },
            );
            if (!response.ok) return [];
            const data = (await response.json()) as {
              results?: {
                url: string;
                ok: boolean;
                title?: string;
                favicon?: string;
              }[];
            };
            return Array.isArray(data.results) ? data.results : [];
          }),
        );

        const resolved: Record<string, SourceMetadata> = {};
        responses.flat().forEach((result) => {
          const metadata = result.ok
            ? {
                title: result.title?.trim() || undefined,
                favicon: result.favicon,
              }
            : {};
          legacySourceMetadataCache.set(result.url, metadata);
          resolved[result.url] = metadata;
        });
        if (!cancelled && Object.keys(resolved).length > 0) {
          setLegacySourceMetadata((previous) => ({
            ...previous,
            ...resolved,
          }));
        }
      } catch (error) {
        console.warn("Could not resolve legacy source titles:", error);
      }
    };

    void resolveMetadata();
    return () => {
      cancelled = true;
    };
  }, [currentUser, sourceGroups]);

  useDraftGalleryAutoFill({
    groupCount: sourceGroups.length,
    targetGroupCount: recentGroupLimit ?? 1,
    stable: timelineWindow.stable,
    hasMore: timelineWindow.hasMore,
    loading: loadingData,
    visiblePostCount: timelineWindow.posts.length,
    limitCount,
    pageSize: PAGE_SIZE,
    setLimitCount,
  });

  const displaySourceGroups = useMemo(
    () =>
      sourceGroups.slice(0, recentGroupLimit).map((group) => {
        const metadata = group.sourceUrl
          ? legacySourceMetadata[group.sourceUrl]
          : undefined;
        if (!metadata) return group;
        return {
          ...group,
          label: metadata.title || group.label,
          favicon: metadata.favicon || group.favicon,
        };
      }),
    [legacySourceMetadata, sourceGroups, recentGroupLimit],
  );

  // Click → first/all finished posts: completes the timing started on the
  // Generate page (postIds arrive when the server responds). The all-post
  // event also exposes the straggler gap, then retires the storage marker.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    for (const request of readPendingRequests(activeWorkspaceId)) {
      const postIds = request.postIds;
      if (!Array.isArray(postIds) || postIds.length === 0) continue;
      const readyPostIds = new Set(
        posts
          .filter((p) => p.status === "draft" && postIds.includes(p.id))
          .map((p) => p.id),
      );
      if (readyPostIds.size === 0) continue;

      const now = Date.now();
      let firstPostSeenAt = request.firstPostSeenAt;
      if (!request.firstPostSeen) {
        firstPostSeenAt = now;
        posthog.capture("generation_first_post_visible", {
          client_request_id: request.clientRequestId,
          duration_ms: now - request.clickedAt,
          post_count: postIds.length,
          generation_quality_variant: request.generationQualityVariant,
        });
      }

      if (!request.error && postIds.every((postId) => readyPostIds.has(postId))) {
        posthog.capture("generation_all_posts_visible", {
          client_request_id: request.clientRequestId,
          duration_ms: now - request.clickedAt,
          post_count: postIds.length,
          generation_quality_variant: request.generationQualityVariant,
          ...(typeof firstPostSeenAt === "number"
            ? { straggler_duration_ms: now - firstPostSeenAt }
            : {}),
        });
        removePendingRequest(activeWorkspaceId, request.clientRequestId);
      } else if (!request.firstPostSeen) {
        updatePendingRequest(activeWorkspaceId, request.clientRequestId, {
          firstPostSeen: true,
          firstPostSeenAt,
        });
      }
    }
  }, [activeWorkspaceId, posts]);

  const loading = Boolean(
    !errorData &&
      !requestCompletionError &&
      (loadingData || !snapshotMatchesQuery || !timelineWindow.stable),
  );
  const error = errorData || requestCompletionError;

  useLayoutEffect(() => {
    if (!draftViewKey || loading || error || restoredDraftViewKey.current === draftViewKey) return;
    const saved = fromGeneration ? undefined : draftViewPositions.get(draftViewKey);
    // Restore the previously loaded rows before restoring a deep scroll position.
    if (saved && (limitCount < saved.limitCount || queryLimit < saved.queryLimit)) return;
    window.scrollTo({ top: saved?.scrollY ?? 0, left: 0, behavior: "auto" });
    restoredDraftViewKey.current = draftViewKey;
  }, [draftViewKey, fromGeneration, loading, error, limitCount, queryLimit]);

  useEffect(() => {
    if (!draftViewKey || restoredDraftViewKey.current !== draftViewKey) return;
    const savePosition = () => {
      draftViewPositions.set(draftViewKey, {
        scrollY: window.scrollY,
        limitCount,
        queryLimit,
      });
    };
    savePosition();
    window.addEventListener("scroll", savePosition, { passive: true });
    // Save on scroll, not unmount: the next page may already have reset scrolling.
    return () => window.removeEventListener("scroll", savePosition);
  }, [draftViewKey, loading, error, limitCount, queryLimit]);

  // Only expose pagination once the creation-time window is stable. A recent
  // duplicate can belong to an older source row, in which case the query
  // silently overfetches until no unseen post could sort ahead of this page.
  useEffect(() => {
    if (
      snapshotMatchesQuery &&
      timelineWindow.stable &&
      !lookaheadAlreadyIncluded
    ) {
      setInternalHasMore(timelineWindow.hasMore);
    } else if (lookaheadAlreadyIncluded) {
      // The exact request listener already supplied the query lookahead. Hide
      // the control while the global query silently advances to a genuinely
      // unseen post (or the end of the collection).
      setInternalHasMore(false);
    } else if (!loadingData && errorData) {
      setInternalHasMore(false); // No data due to error
    } else if (!loadingData && !snapshot) {
      // Check snapshot
      setInternalHasMore(false);
    }
  }, [
    snapshot,
    snapshotMatchesQuery,
    timelineWindow.hasMore,
    timelineWindow.stable,
    loadingData,
    errorData,
    lookaheadAlreadyIncluded,
  ]);

  // Load media URLs incrementally (only for new posts)
  useEffect(() => {
    const loadUrls = async () => {
      if (posts.length > 0) {
        // Filter to only posts that have paths we haven't loaded yet
        const postsToLoad = posts.filter((post) =>
          post.mediaItems?.some((mediaItem) => {
            if (!mediaItem) return false;
            if (!mediaUrls[mediaItem.path]) return true;
            if (
              mediaItem.type === "video" &&
              mediaItem.metadata?.thumbnailPath &&
              !mediaUrls[mediaItem.metadata.thumbnailPath]
            )
              return true;
            if (mediaItem.metadata?.overlays) {
              for (const overlay of mediaItem.metadata.overlays) {
                if (
                  overlay.type === "image" &&
                  "filePath" in overlay &&
                  overlay.filePath &&
                  !mediaUrls[overlay.filePath]
                )
                  return true;
              }
            }
            return false;
          }),
        );

        if (postsToLoad.length > 0) {
          const urlEntries = await loadMediaUrls(postsToLoad);
          setMediaUrls((prev) => ({ ...prev, ...urlEntries }));
        }
      }
    };
    loadUrls();
  }, [posts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore the viewport before the expanded timeline paints. A normal effect
  // plus requestAnimationFrame briefly showed the browser's scroll anchoring
  // position first, which looked like a full-page flicker.
  useLayoutEffect(() => {
    if (!isLoadingMore) {
      previousPostCountRef.current = posts.length;
      return;
    }
    if (loading) return;

    if (posts.length > previousPostCountRef.current) {
      window.scrollTo(0, savedScrollPosition);
    }
    previousPostCountRef.current = posts.length;
    setIsLoadingMore(false);
  }, [isLoadingMore, loading, posts.length, savedScrollPosition]);

  const beginLoadingMore = () => {
    setSavedScrollPosition(window.scrollY);
    setIsLoadingMore(true);
  };

  const handleLoadMore = () => {
    if (loading || !internalHasMore) return;
    beginLoadingMore();
    // Exact request completion may have already rendered posts beyond the raw
    // query cutoff. Advance past those posts so one click always reaches new
    // rows instead of re-fetching cards already on screen.
    const nextLimit =
      Math.max(limitCount, timelineWindow.posts.length) + PAGE_SIZE;
    setLimitCount(nextLimit);
    setQueryLimit((currentLimit) => Math.max(currentLimit, nextLimit));
  };

  const openBlankPost = () =>
    navigate("/create-post", {
      state: editorNavigationState(location, true),
    });

  if (!currentUser) {
    return <div>Please log in to view content.</div>;
  }

  if (loading && posts.length === 0 && pendingRequests.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div className="h-10 w-10 rounded-full border-2 border-gray-300 border-t-[#FF5C35] animate-spin" />
          </div>
          <p className="text-gray-600">Loading posts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center bg-red-50 p-6 rounded-xl max-w-md">
          <h3 className="font-semibold text-red-700 mb-2">
            Error loading posts
          </h3>
          <p className="text-sm text-red-600 mb-4">{error.message}</p>
          <Button
            onClick={() => {
              window.location.reload(); // Simple retry: reload the page
            }}
            variant="outline"
            className="border-red-200 hover:bg-red-100 text-red-700"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "p-4 sm:p-6"}>
      {!embedded && (
        <div className="mb-4 flex flex-col gap-3 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-xl font-semibold text-gray-900">Drafts</h1>
          {posts.length > 0 && (
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
              <Button
                variant="outline"
                className="min-h-11"
                onClick={openBlankPost}
              >
                <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                Blank post
              </Button>
              <Button
                className="min-h-11"
                onClick={() => navigate("/generate")}
              >
                <Sparkles className="mr-1 h-4 w-4" aria-hidden="true" />
                Generate
              </Button>
            </div>
          )}
        </div>
      )}

      {embedded && (
        <DraftGuidance
          workspaceId={activeWorkspaceId}
          hasReadyDraft={displaySourceGroups.some((group) => group.posts.some((post) => post.status === "draft"))}
          hasUsableConnection={workspaceConnections.some((connection) => connection.status === "active" || connection.status === "expiring_soon")}
          isFreePlan={isFreePlan}
          loading={loading || planLoading || connectionsLoading || !!connectionsError}
          onConnect={() => navigate("/settings")}
          onUpgrade={checkoutContinuation ? handleCheckoutContinuation : () => navigateToUpgrade({ reason: "recent_drafts_guidance" })}
          upgradeLabel={checkoutContinuation ? "Continue checkout" : "Upgrade"}
        />
      )}

      {!embedded &&
        !creditsBannerDismissed &&
        searchParams.get("generated") &&
        (() => {
          const generatedCount = searchParams.get("generated");
          const postLabel = `${generatedCount} post${generatedCount !== "1" ? "s" : ""} created.`;
          const hasConnections = Object.keys(connectionsById).length > 0;
          const message = hasConnections
            ? `${postLabel} Upgrade to keep creating - with more credits and watermark-free posts.`
            : `${postLabel} Connect an account to start publishing — no posts go out without your approval.`;

          return (
            <div className="mb-6 rounded-xl px-4 py-3 flex items-center justify-between bg-[#FBF6F1] border border-[#EFE5DA]">
              <div className="flex items-center gap-2.5 min-w-0">
                <Sparkles className="h-4 w-4 text-[#FF5C35] shrink-0" />
                <p className="text-sm font-medium text-foreground truncate">
                  {message}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-4">
                <Button
                  size="sm"
                  onClick={
                    !hasConnections
                      ? () => navigate("/settings")
                      : checkoutContinuation
                        ? handleCheckoutContinuation
                        : () => navigateToUpgrade({ reason: "created_banner" })
                  }
                >
                  {!hasConnections
                    ? "Connect an account"
                    : checkoutContinuation
                      ? "Continue checkout"
                      : "Upgrade"}
                </Button>
                <button
                  onClick={() => {
                    setCreditsBannerDismissed(true);
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete("generated");
                    setSearchParams(newParams, { replace: true });
                  }}
                  className="p-1 rounded-full transition-colors hover:bg-black/5 text-muted-foreground hover:text-foreground"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })()}

      {embedded && !loading && !error && displaySourceGroups.length === 0 && (
        <div className="rounded-lg bg-muted/30 px-6 py-10 text-center">
          <p className="text-sm font-medium">Your drafts will appear here.</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Create a post from your content, or choose an idea above.
          </p>
        </div>
      )}
      {!embedded && !loading && !error && displaySourceGroups.length === 0 && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md">
            <div className="mb-4">
              <ImageIcon className="h-12 w-12 text-gray-400 mx-auto" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">No posts yet</h3>
            <p className="text-sm text-gray-500 mb-6">
              Generated posts will first appear here. Generate from an article
              URL, YouTube link, Google Doc, or text, or start from scratch.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button
                onClick={() => navigate("/generate")}
                className="shadow-sm hover:shadow-md transition-all duration-200"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Generate Content
              </Button>
              <Button variant="outline" onClick={openBlankPost}>
                <Plus className="h-4 w-4 mr-1" />
                Blank Post
              </Button>
            </div>
          </div>
        </div>
      )}

      {!error && displaySourceGroups.length > 0 && (
        <>
          <div className="flex flex-col gap-10">
            {displaySourceGroups.map((group, groupIndex) => {
              const SourceIcon =
                group.sourceType === "manual"
                  ? Pencil
                  : SOURCE_ICONS[group.sourceType];
              const generatingCount = group.posts.filter(
                (post) => post.status === "generating",
              ).length;
              const readyCount = group.posts.length - generatingCount;
              const showPendingTile = showSourcePlaceholder(group);
              const shelfItemCount =
                group.posts.length + (showPendingTile || group.isConfirming || group.failedRequest ? 1 : 0);
              const pendingSource: PendingSource = {
                type: group.sourceType === "manual" ? "text" : group.sourceType,
                label: group.label,
                favicon: group.favicon,
                generationSource: group.sourceUrl,
              };
              const sourceRowId = `draft-source-row-${groupIndex}`;
              const sourceShelfId = `draft-source-shelf-${groupIndex}`;

              return (
                <section
                  key={group.key}
                  aria-labelledby={sourceRowId}
                  className="min-w-0"
                >
                  <HorizontalPostShelf
                    id={sourceShelfId}
                    label={group.label}
                    contentVersion={shelfItemCount}
                    fill={showPendingTile}
                    header={
                      <div className="flex min-w-0 items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-background">
                          {group.favicon ? (
                            <img
                              src={group.favicon}
                              alt=""
                              className="h-4 w-4 rounded-[3px]"
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <SourceIcon
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                          )}
                        </div>
                        <div className="min-w-0">
                          {group.sourceUrl ? (
                            <a
                              id={sourceRowId}
                              href={group.sourceUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="block truncate text-sm font-semibold text-foreground hover:text-brand-ink hover:underline"
                            >
                              {group.label}
                            </a>
                          ) : (
                            <h2
                              id={sourceRowId}
                              className="truncate text-sm font-semibold text-foreground"
                            >
                              {group.label}
                            </h2>
                          )}
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {group.sourceType === "manual"
                              ? "Created"
                              : group.isPreparing || generatingCount > 0
                                ? "Started"
                                : "Generated"}{" "}
                            {formatGenerationTime(group.generatedAt)}
                          </p>
                        </div>
                      </div>
                    }
                    meta={
                      <p className="text-xs font-medium text-muted-foreground">
                        {group.failedRequest ? "Couldn’t prepare posts" : group.isConfirming ? "Still confirming your request" : group.isPreparing
                          ? group.posts.length > 0
                            ? `${group.posts.length} found · still planning`
                            : group.expectedPosts && group.expectedPosts > 1
                              ? `Making up to ${group.expectedPosts} posts`
                              : "Making your posts"
                          : generatingCount > 0
                            ? `${readyCount} of ${group.posts.length} ready`
                            : `${group.posts.length} post${group.posts.length === 1 ? "" : "s"}`}
                      </p>
                    }
                  >
                    {group.posts.map((post) => {
                      const connectionStatuses = Object.values(
                        post.connections,
                      );
                      const hasPublished = connectionStatuses.some(
                        (conn) => conn.status === "published",
                      );
                      const isProcessing = connectionStatuses.some(
                        (conn) =>
                          conn.status === "publishing" ||
                          conn.status === "pre-processing",
                      );
                      const isEditable = isPostEditable(post);
                      const derivedStatus = derivePostStatus(post.connections);
                      const showPublish =
                        post.status === "draft" && isEditable && !isProcessing;
                      // Everything here is a draft, so the badge only shows other states.
                      const showStatusBadge = !(showPublish && derivedStatus === "draft");

                      return (
                        <div
                          key={post.id}
                          className="group min-w-0 snap-start overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-200 hover:shadow-md"
                        >
                          <div className="relative aspect-square">
                            {post.status === "generating" ? (
                              <GeneratingPostMedia postId={post.id} />
                            ) : post.mediaItems &&
                              post.mediaItems.length > 0 ? (
                              <MediaGallery
                                expandedPost={post}
                                connectionsById={connectionsById}
                                mediaUrls={mediaUrls}
                              />
                            ) : (
                              <div
                                className="h-full bg-gray-50 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 cursor-pointer"
                                onClick={() => {
                                  setSelectedPostForViewer(post);
                                  setIsViewerOpen(true);
                                }}
                              >
                                <ImageIcon className="h-12 w-12 text-gray-300 mb-2" />
                                <div className="text-gray-400 text-sm font-medium">
                                  No media added
                                </div>
                              </div>
                            )}

                            <div className="absolute top-4 left-4 flex items-center">
                              <div className="flex -space-x-2">
                                {Object.values(post.connections)
                                  .slice(0, 3)
                                  .map((connection) => (
                                    <div key={connection.connectionId}>
                                      <PlatformConnectionIcon
                                        platform={
                                          connection.platform as PlatformType
                                        }
                                        profileImage={
                                          connectionsById[
                                            connection.connectionId
                                          ]?.profileImage
                                        }
                                        profileName={
                                          connectionsById[
                                            connection.connectionId
                                          ]?.profileName
                                        }
                                        size="sm"
                                        disableTooltip={true}
                                      />
                                    </div>
                                  ))}
                              </div>
                              {Object.keys(post.connections).length > 3 && (
                                <span className="ml-1 bg-white border border-gray-200 text-gray-700 text-xs font-medium px-1.5 py-0.5 rounded-full">
                                  +{Object.keys(post.connections).length - 3}
                                </span>
                              )}
                            </div>

                            <div
                              className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    className="h-8 w-8 bg-white hover:bg-gray-50 border border-gray-200"
                                  >
                                    <MoreVertical className="h-4 w-4 text-gray-700" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="w-48"
                                >
                                  <DropdownMenuItem
                                    onClick={() => handleEdit(post.id)}
                                    disabled={!isEditable}
                                    className={
                                      !isEditable ? "text-muted-foreground" : ""
                                    }
                                  >
                                    <Pencil className="h-4 w-4 mr-1" />
                                    {post.status === "generating"
                                      ? "Generating..."
                                      : isProcessing
                                        ? "Processing..."
                                        : "Edit"}
                                  </DropdownMenuItem>
                                  {showPublish && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleEdit(post.id, { intent: "publish", from: "drafts_card" })
                                      }
                                    >
                                      <Send className="h-4 w-4 mr-1" />
                                      Publish
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => handleDuplicate(post.id)}
                                    disabled={post.status === "generating"}
                                    className={
                                      post.status === "generating"
                                        ? "text-muted-foreground"
                                        : ""
                                    }
                                  >
                                    <Copy className="h-4 w-4 mr-1" />
                                    Duplicate
                                  </DropdownMenuItem>
                                  {!isProcessing &&
                                    post.status !== "generating" &&
                                    getDeleteDialog(
                                      post,
                                      hasPublished,
                                      <DropdownMenuItem
                                        className="text-red-600 focus:text-red-600 focus:bg-red-50 gap-2"
                                        onSelect={(e) => e.preventDefault()}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        Delete
                                      </DropdownMenuItem>,
                                    )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>

                          <div
                            className={cn(
                              "p-3",
                              post.status !== "generating" && "cursor-pointer",
                            )}
                            onClick={() => {
                              if (post.status === "generating") return;
                              setSelectedPostForViewer(post);
                              setIsViewerOpen(true);
                            }}
                          >
                            {/* One line, the same on every card: the cover
                                already shows the headline, and a reserved
                                second line left an odd gap under short titles. */}
                            <div className="mb-3 min-h-[1.25rem]">
                              {(post.title || post.description) && (
                                <>
                                  <p
                                    translate="no"
                                    title={post.title || post.description}
                                    className={cn(
                                      "notranslate text-gray-700 text-sm",
                                      !expandedDescriptions.has(post.id) &&
                                        "line-clamp-1",
                                    )}
                                  >
                                    {post.title || post.description}
                                  </p>
                                  {(post.title || post.description || "")
                                    .length > 150 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleDescription(post.id);
                                      }}
                                      className="text-sm text-gray-500 hover:text-gray-700 mt-1"
                                    >
                                      {expandedDescriptions.has(post.id)
                                        ? "Show less"
                                        : "Show more"}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>

                            <div className="flex items-center justify-between gap-2 text-sm text-gray-500">
                              {showStatusBadge && <StatusBadge
                                status={derivedStatus}
                                mixedStatusCounts={
                                  derivedStatus === "mixed"
                                    ? Object.values(post.connections).reduce(
                                        (acc, conn) => {
                                          acc[conn.status] =
                                            (acc[conn.status] || 0) + 1;
                                          return acc;
                                        },
                                        {} as Record<string, number>,
                                      )
                                    : undefined
                                }
                                errorInfo={
                                  derivedStatus === "failed" &&
                                  Object.values(post.connections).some(
                                    (conn) => conn.error,
                                  )
                                    ? {
                                        code: Object.values(
                                          post.connections,
                                        ).find((conn) => conn.error)?.error
                                          ?.code,
                                        message: Object.values(
                                          post.connections,
                                        ).find((conn) => conn.error)?.error
                                          ?.message,
                                        timestamp:
                                          Object.values(post.connections).find(
                                            (conn) => conn.error,
                                          )?.error?.timestamp || 0,
                                        retryCount:
                                          Object.values(post.connections).find(
                                            (conn) => conn.error,
                                          )?.error?.retryCount || 0,
                                        retryable: Object.values(
                                          post.connections,
                                        ).find((conn) => conn.error)?.error
                                          ?.retryable,
                                        maxAttempts: 5,
                                        cancelled: Object.values(
                                          post.connections,
                                        ).find((conn) => conn.error)?.error
                                          ?.cancelled,
                                      }
                                    : undefined
                                }
                              />}
                              {post.status === "generating" ? (
                                <span className="text-sm text-gray-400">
                                  Generating...
                                </span>
                              ) : isProcessing ? (
                                <span className="text-sm text-gray-400">
                                  Processing...
                                </span>
                              ) : showPublish ? (
                                // Publishing is the next step for a draft; the
                                // modal connects an account in place if needed.
                                <div
                                  className={cn(
                                    "grid min-w-0 grid-cols-[2.25rem_minmax(0,1fr)] gap-2",
                                    showStatusBadge ? "shrink-0" : "w-full",
                                  )}
                                >
                                  <button
                                    type="button"
                                    aria-label="Edit"
                                    title="Edit"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(post.id);
                                    }}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                  >
                                    <Pencil className="h-4 w-4" aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(post.id, { intent: "publish", from: "drafts_card" });
                                    }}
                                    className="inline-flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-lg bg-[oklch(var(--brand)/0.1)] px-2 text-sm font-semibold text-brand-ink transition-colors hover:bg-brand hover:text-brand-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                  >
                                    <Send className="h-3.5 w-3.5" aria-hidden="true" />
                                    Publish
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(post.id);
                                  }}
                                  className="group inline-flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                                >
                                  <span>Edit</span>
                                  <ArrowRight
                                    className="h-4 w-4 text-[#FF5C35] transition-transform duration-150 ease-out group-hover:translate-x-0.5"
                                    aria-hidden="true"
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {group.failedRequest && (
                      <GenerationFailureCard failure={group.failedRequest} label={group.label}
                        onRecover={paste => navigate(`/generate?retryRequest=${encodeURIComponent(group.failedRequest!.requestId)}${paste ? "&recovery=paste" : ""}`)}
                        onUpgrade={() => navigateToUpgrade({ reason: "no_credits" })}
                        onDismiss={() => { if (activeWorkspaceId) removePendingRequest(activeWorkspaceId, group.failedRequest!.requestId); }} />
                    )}
                    {group.isConfirming && (
                      <div role="status" className="w-80 shrink-0 rounded-lg border border-border p-5 text-sm">
                        <p className="font-medium">Still confirming your request</p>
                        <p className="mt-2 text-muted-foreground">Your posts may still be processing. Check back here before submitting this content again.</p>
                      </div>
                    )}
                    {showPendingTile && (
                      <PendingGenerationTile
                        source={pendingSource}
                        startedAt={group.pendingStartedAt ?? group.generatedAt}
                        stage={group.stage}
                      />
                    )}
                  </HorizontalPostShelf>
                </section>
              );
            })}
          </div>
        </>
      )}

      {!error &&
        !!recentGroupLimit &&
        displaySourceGroups.length >= recentGroupLimit &&
        (sourceGroups.length > recentGroupLimit || timelineWindow.hasMore) && (
          <div className="mt-6 flex justify-center">
            <Link
              to="/created"
              className="inline-flex min-h-11 items-center gap-2 rounded-md px-4 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              All drafts <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        )}

      {!recentGroupLimit &&
        (internalHasMore || isLoadingMore) &&
        (embedded || displaySourceGroups.length > 0) && (
          <div className="mt-8 text-center">
            <Button
              onClick={handleLoadMore}
              variant="outline"
              className="min-h-11 min-w-40"
              disabled={loading || isLoadingMore}
              aria-busy={isLoadingMore}
            >
              {isLoadingMore && (
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin"
                  aria-hidden="true"
                />
              )}
              {isLoadingMore ? "Loading posts…" : "Load more posts"}
            </Button>
          </div>
        )}

      {isViewerOpen && selectedPostForViewer && (
        <PostViewer
          isOpen={isViewerOpen}
          onClose={() => {
            setIsViewerOpen(false);
            setSelectedPostForViewer(null);
          }}
          expandedPost={selectedPostForViewer}
          initialIndex={0}
          connectionsById={connectionsById}
        />
      )}
    </div>
  );
}
